// ============================================================
// THE OBJECT'S OWN FIELDS (2026-10-26, plan cover-cue-and-objects).
//
// An Object body - a generator, a shrine, a door to hold - carries, beside its Wounds, Armour, Toughness and
// Size: whether it COUNTS AS TERRAIN (and then the exact box it fills and the material it is made of), and what it
// leaves when destroyed (rubble, or nothing). Read defensively: a sheet arrives from storage, a file or the wire.
//
// Pure. Zero React dependency.
// ============================================================
import { DEFAULT_MATERIALS } from '../battlefield/voxels';

/** What an Object body adds to the sheet. */
export interface ObjectSpec {
  /** Its body is a box on the map: it blocks sight, gives cover, stops movement - what you see is what blocks. */
  terrain: boolean;
  /** The box, in metres; absent for a mark-only object (sized from its Size like every figure). */
  box?: { w: number; d: number; h: number };
  /** A key of DEFAULT_MATERIALS: what the box is made of (its cover AP, whether it can be seen through). */
  material: string;
  /** What is left once it is destroyed: rubble (low cover, difficult ground) or nothing. */
  remains: 'rubble' | 'gone';
}

/** An Object with nothing set: mark-only, stone, leaving rubble. */
export const DEFAULT_OBJECT_SPEC: ObjectSpec = { terrain: false, material: 'stone', remains: 'rubble' };

const positive = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null);

function boxOf(raw: unknown): ObjectSpec['box'] | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const [w, d, h] = [positive(r.w), positive(r.d), positive(r.h)];
  return w && d && h ? { w, d, h } : undefined;
}

/**
 * An Object's fields from whatever arrived, or the defaults. A box only when all three sides are positive numbers
 * - spread conditionally, never stored as undefined (Firestore refuses the document, gotcha 2).
 */
export function readObjectSpec(raw: unknown): ObjectSpec {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_OBJECT_SPEC };
  const r = raw as Record<string, unknown>;
  const box = boxOf(r.box);
  const material = typeof r.material === 'string' && r.material in DEFAULT_MATERIALS ? r.material : 'stone';
  return {
    terrain: r.terrain === true,
    ...(box ? { box } : {}),
    material,
    remains: r.remains === 'gone' ? 'gone' : 'rubble',
  };
}

/** The box of an Object that counts as terrain, or null: a mark-only object, any other body, or nobody. */
export function terrainBoxOf(
  sheet: { bodyType?: string; objectSpec?: unknown } | null | undefined,
): ObjectSpec['box'] | null {
  if (sheet?.bodyType !== 'object') return null;
  const spec = readObjectSpec(sheet.objectSpec);
  return spec.terrain && spec.box ? spec.box : null;
}

/** How wide a figure is drawn, metres, or null for its footprint: the sheet's Token size, when it is set. */
export function drawnDiameterOf(tokenDiameter: number | null | undefined): number | null {
  return tokenDiameter && tokenDiameter > 0 ? tokenDiameter : null;
}
